import { Link } from "react-router-dom";
import type { ReactElement } from "react";

import { useScrollReveal } from "../hooks/useScrollReveal";

const STEPS = [
  { num: "01", title: "Consent", desc: "Review and complete the research consent form." },
  { num: "02", title: "Brief Survey", desc: "Answer a few demographic and health questions." },
  { num: "03", title: "Photo Capture", desc: "Take photos using your device or upload existing images." },
] as const;


export function Landing(): ReactElement {
  useScrollReveal();

  return (
    <>
      <div className="bg-orbs" aria-hidden="true">
        <div className="bg-orb bg-orb-1" />
        <div className="bg-orb bg-orb-2" />
        <div className="bg-orb bg-orb-3" />
      </div>

      {/* Navbar */}
      <nav className="site-nav" role="navigation">
        <div className="nav-brand">
          <a
            href="https://aim.hms.harvard.edu/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Artificial Intelligence in Medicine Program"
          >
            <img src="/media/aim_logo.webp" alt="AIM Program" />
          </a>
          <div className="nav-divider" />
          <Link to="/" className="nav-brand-name">
            FaceAge
          </Link>
        </div>
        <div className="nav-actions">
          <a href="#about-title" className="nav-link-text">
           Our Research 
          </a>
          <Link to="/login" className="btn-nav">
            Returning User
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero" aria-label="Hero">
        <h1>
          Your face reveals
          <br />
          your <span className="highlight">biological age</span>
        </h1>

        <p className="hero-sub">
          Advanced Biological Age Estimation through AI Technology.
        </p>

        <div className="hero-ctas">
          <Link to="/register" className="btn-primary-glow">
            <i className="fas fa-user-plus" aria-hidden="true" />
            Join the Study
          </Link>
          <Link to="/login" className="btn-ghost">
            <i className="fas fa-sign-in-alt" aria-hidden="true" />
            Returning User 
          </Link>
        </div>

      </section>

      {/* How it works */}
      <section className="section section-tight" aria-labelledby="how-title">
        <div className="section-inner">
          <div className="reveal">
            <div className="section-eyebrow">Protocol</div>
            <h2 className="section-title" id="how-title">
              How it works
            </h2>
            <p className="section-body">
              Participating takes less than 10 minutes. Follow these three steps to receive your
              biological age estimation.
            </p>
          </div>

          <div className="steps-grid reveal">
            {STEPS.map((step, i) => (
              <div className="step-card" key={step.num}>
                <div className="step-num-badge">{step.num}</div>
                <div className="step-title">{step.title}</div>
                <div className="step-desc">{step.desc}</div>
                {i < STEPS.length - 1 ? <div className="step-connector" aria-hidden="true" /> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* About */}
      <section className="section" aria-labelledby="about-title">
        <div className="section-inner">
          <div className="reveal">
            <div className="section-eyebrow">Our Research</div>
            <h2 className="section-title" id="about-title">
              What is FaceAge?
            </h2>
          </div>

          <div className="about-grid">
            <div className="about-content reveal">
              <h3>
                <i className="fas fa-user-circle" /> Biological Age vs. Chronological Age
              </h3>
              <p>
                We use an advanced AI model, FaceAge, to estimate your biological age. 
                Biological age reflects your body's physiological condition and aging process rather than your actual chronological age. 
              </p>

              <h3>
                <i className="fas fa-heartbeat" /> Why does this matter?
              </h3>
              <p>
                Research shows that people who look older than their actual age may have higher health risks, 
                while those who look younger often have better health and longer lifespans. Our work has demonstrated that FaceAge can be used to accurately predict survival in patients with chronic diseases.
                <sup className="cite-sup">
                  <a
                    href="https://www.thelancet.com/journals/landig/article/PIIS2589-7500(25)00042-1/fulltext"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-plain"
                  >
                    1
                  </a>
                </sup>

                Understanding your biological age can help you make informed decisions about your health and lifestyle. 
                It provides a unique window into your body's aging process and can serve as a valuable health indicator. 
              </p>

              <h3>
                <i className="fas fa-microscope" /> What will this study help us understand?
              </h3>
              <p>
                By participating, you help us explore how biological age relates to lifestyle choices, health conditions, and long-term well-being. 
                Your results might offer insights into factors that influence aging and health outcomes.
                
                Your participation contributes to advancing our understanding of aging and health. 
              </p>

              <h3>
                <i className="fas fa-shield-alt" /> Your privacy is protected
              </h3>
              <p>
               We take your privacy seriously. All photos and data are securely stored and used only for research purposes. 
               Your identity is protected, and you have full control over your participation
              </p>
            </div>

            <div className="reveal">
              <div className="peer-reviewed-card">
                <div className="peer-reviewed-badge">Peer Reviewed</div>
                <div className="peer-reviewed-title">
                  FaceAge, a deep learning system to estimate biological age from face photographs to
                  improve prognostication: a model development and validation study
                </div>
                <div className="peer-reviewed-journal">The Lancet Digital Health — 2025</div>
                <a
                  href="https://www.thelancet.com/journals/landig/article/PIIS2589-7500(25)00042-1/fulltext"
                  className="peer-reviewed-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the paper
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </a>
              </div>

              <div className="reveal">
              <div className="peer-reviewed-card">
                <div className="peer-reviewed-badge">Peer Reviewed</div>
                <div className="peer-reviewed-title">
                  Face aging rate quantifies change in biological age to predict cancer outcomes
                </div>
                <div className="peer-reviewed-journal">Nature Communications — 2026</div>
                <a
                  href="https://www.nature.com/articles/s41467-025-66758-w"
                  className="peer-reviewed-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the paper
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </a>
              </div>
              </div>

              <div className="glass-card mt-4">
                <div className="section-eyebrow text-2xs">
                  How it works
                </div>
                <p className="body-note">
                  FaceAge was trained on nearly{" "}
                  <strong>750,000 face photos</strong>{" "}
                  from diverse backgrounds. It analyzes facial features to estimate age and identify health-related aging patterns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Track */}
      <section className="section" aria-labelledby="track-title">
        <div className="section-inner">
          <div className="reveal">
            <div className="section-eyebrow">Features</div>
            <h2 className="section-title" id="track-title">
              Track your FaceAge over time
            </h2>
            <p className="section-body">
              Return daily or upload historical photos to build a longitudinal picture of your
              biological aging.
            </p>
          </div>

          <div className="feature-grid">
            <div className="feature-card reveal">
              <div className="feature-icon">
                <i className="fas fa-camera" aria-hidden="true" />
              </div>
              <div className="feature-title">Daily FaceAge Variation</div>
              <div className="feature-desc">
                See how your FaceAge changes daily.
              </div>
              <ul className="feature-list">
                <li>Take photos using your device, or upload existing images</li>
                <li>Receive your FaceAge estimation via email</li>
                <li>You can return every day to see how your FaceAge changes</li> 
              </ul>
              <footer className="card-footnote">
                Perfect for tracking how lifestyle factors like sleep, 
                stress, and daily activities affect your FaceAge estimation.
              </footer>
            </div>
            <div className="feature-card reveal">
              <div className="feature-icon">
                <i className="fas fa-images" aria-hidden="true" />
              </div>
              <div className="feature-title">Historical FaceAge Variation</div>
              <div className="feature-desc">
                See how your FaceAge has changed throughout the years.
              </div>
              <ul className="feature-list">
                <li>Upload photos from different stages of your life</li>
                <li>Receive your FaceAge estimations via email</li>
                <li>See how your FaceAge has changed over time.</li>
              </ul>
              <footer className="card-footnote">
                Ideal for understanding long-term aging patterns 
                and the impact of major life events on your FaceAge estimation.
              </footer>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="disclaimer" role="note" aria-label="Important disclaimer">
        <div className="disclaimer-inner">
          <div className="disclaimer-head">
            <i className="fas fa-exclamation-triangle disclaimer-icon" aria-hidden="true" />
            <h2 className="disclaimer-title">Important Disclaimer</h2>
          </div>
          <div className="disclaimer-body">
            <div className="disclaimer-item">
              <h3>Medical Emergency?</h3>
              <p>
                Do not use this website/application for emergencies. If you think you have a medical
                emergency, call your doctor or dial 911 immediately.
              </p>
            </div>
            <div className="disclaimer-item">
              <h3>Non-Medical Advice Notice</h3>
              <p>
                This website/application does not provide medical advice. Use of the
                website/application does not establish a doctor–patient relationship.
              </p>
            </div>
            <div className="disclaimer-item">
              <h3>Educational Purpose Only</h3>
              <p>
                This website/application is provided to the general public for reference and
                educational purposes only. The content is not meant to be complete or exhaustive or
                to be applicable to any specific individual's medical condition.
              </p>
            </div>
            <div className="disclaimer-item">
              <h3>Not a Substitute for Professional Care</h3>
              <p>
                The content on this website/application is not intended to be a substitute for
                professional medical advice, diagnosis or treatment. Always consult with a qualified
                and licensed physician or other medical care provider, and follow their advice
                without delay regardless of anything read on this website/application.
              </p>
            </div>
            <div className="disclaimer-item wide">
              <h3>Emergency Response Limitation</h3>
              <p>
                Do not rely on communication through this website/application for immediate, urgent
                medical needs. This website/application is not designed to facilitate medical
                emergencies. Users should not rely upon this website/application for emergency
                medical treatment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Media coverage */}
      <div className="media-section" aria-label="Media coverage">
        <div className="media-eyebrow">As seen in</div>
        <div className="media-logos">
          {[
            ["The Wall Street Journal", "/media/wsj_logo.webp", "https://www.wsj.com/health/wellness/face-age-test-health-medicine-6ca1a4c5"],
            ["The New York Times", "/media/nyt_logo.webp", "https://www.nytimes.com/2025/05/08/well/biological-age-faceage.html"],
            ["The Boston Globe", "/media/globe_logo.webp", "https://www.bostonglobe.com/2025/05/09/metro/aging-ai-artificial-intelligence-cancer/"],
            ["The Washington Post", "/media/wapo_logo.webp", "https://www.washingtonpost.com/science/2025/05/12/ai-tool-biological-age-faceage/"],
            ["CNN", "/media/cnn_logo.webp", "https://www.youtube.com/watch?v=lM9P3dZD_A0"],
          ].map(([name, src, href]) => (
            <a
              key={name}
              href={href}
              className="media-logo-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={src} alt={name} loading="lazy" />
            </a>
          ))}
        </div>
      </div>

      {/* Institution bar */}
      <div className="institution-bar" aria-label="Institutional affiliations">
        <div className="institution-bar-inner">
          {[
            ["Research Lab", "AIM Program"],
            ["Hospital", "Brigham and Women's"],
            ["Health System", "Mass General Brigham"],
            ["University", "Harvard Medical School"],
          ].map(([label, name]) => (
            <div className="institution-item" key={name}>
              <div>
                <div className="institution-label">{label}</div>
                <div className="institution-name">{name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Footer */}
      <footer className="site-footer" role="contentinfo">
        <div className="footer-text">
          AIM Program, Mass General Brigham · Harvard Medical School
          <br />
          75 Francis Street, Boston, MA 02115
          <br />
          <a href="mailto:bwhfaceage@mgb.org" className="link-plain">
            bwhfaceage@mgb.org
          </a>
        </div>
        <div className="footer-links">
          <a href="#about-title">Our Research</a>
          <a href="https://aim.mgh.harvard.edu/" target="_blank" rel="noopener noreferrer">
            AIM Lab
          </a>
        </div>
      </footer>
    </>
  );
}
